import { getFirebase } from '@/services/firebaseApp'

import { isEmailAvailable } from '@/features/Auth/session/views'
import { CredentialProvider, ProviderFailure } from './types'

export type EmailInput = {
  email: string
  password: string
  /**
   * `signUp` creates the account, falling back to signing in. `signIn` never creates one —
   * a login screen that quietly registered whoever mistyped their address would be worse
   * than refusing them.
   */
  intent?: 'signIn' | 'signUp'
}

/**
 * E-mail and password as a credential source.
 *
 * Creation is attempted first and sign-in is the fallback, not the other way round, because
 * `auth/email-already-in-use` is unambiguous. Sign-in-first cannot tell "no such user" from
 * "wrong password" once Firebase's e-mail enumeration protection is on — both arrive as
 * `auth/invalid-credential` — so it could not decide whether to create.
 *
 * The fallback is what lets someone return to an abandoned signup. The client no longer
 * deletes the Firebase account when a signup form is backed out of (EPIC-004: one project
 * serves all three environments, and the client cannot be restricted by environment), so the
 * address stays taken while no profile exists for it. Signing into the account they already
 * own is the way back in.
 */

/**
 * Signing in to an account that must already exist.
 *
 * The address is checked against this server first so "no account here" can be said plainly.
 * Firebase cannot answer it once e-mail enumeration protection is on — "no such user" and
 * "wrong password" both arrive as `auth/invalid-credential` — so without the check every
 * failure would collapse into one unhelpful message.
 */
async function signInOnly(email: string, password: string) {
  if (await isEmailAvailable(email)) {
    throw new ProviderFailure('email', 'rejected', 'No account here uses this address')
  }

  const firebase = await getFirebase()

  try {
    const existing = await firebase.auth().signInWithEmailAndPassword(email.trim(), password.trim())

    return { kind: 'session' as const, provider: 'email' as const, uid: existing.user.uid }
  } catch (error) {
    const code = (error as { code?: string })?.code

    if (code === 'auth/user-disabled') {
      throw new ProviderFailure('email', 'rejected', 'This account has been disabled', { cause: error })
    }

    throw new ProviderFailure('email', 'rejected', 'That address and password do not match an account', {
      cause: error
    })
  }
}

export const emailProvider: CredentialProvider<EmailInput> = {
  id: 'email',

  async authenticate({ email, password, intent = 'signUp' }) {
    if (intent === 'signIn') return signInOnly(email, password)

    const firebase = await getFirebase()

    try {
      const created = await firebase.auth().createUserWithEmailAndPassword(email, password)

      return { kind: 'session', provider: 'email', uid: created.user.uid }
    } catch (error) {
      if ((error as { code?: string })?.code !== 'auth/email-already-in-use') {
        throw new ProviderFailure('email', 'rejected', 'Could not create an account for this address', {
          cause: error
        })
      }
    }

    try {
      const existing = await firebase.auth().signInWithEmailAndPassword(email, password)

      return { kind: 'session', provider: 'email', uid: existing.user.uid }
    } catch (error) {
      throw new ProviderFailure('email', 'conflict', 'This address already belongs to another account', {
        cause: error
      })
    }
  }
}
